import { useLang } from 'hooks/lang';

import { User, FullProjectIssue, isIssueOpen, actions as mutations } from 'entities';

import {
  Row,
  Skeleton,
  Column,
  DropdownMenu,
  DropdownMenuItem,
  Card,
  Box,
} from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { PlusIcon, ListToDoIcon } from 'components/Icons';

import { useJustification } from 'providers/Justification';
import { useUser } from 'hooks/user';

import { getTimestampDisplay } from 'utils/date';
import { OTHER, actions } from '../utils';

import { BaseProps } from './types';

export function ActionsTaken({ issue, updateIssue }: Omit<BaseProps, 'close' | 'setTab'>) {
  const { t } = useLang();
  const user = useUser();

  const { openJustificationModal } = useJustification();

  const availableActions = actions.filter(
    ({ id }) => !issue?.actions.find(({ action }) => (action === OTHER ? false : action === id)),
  ); // create options;

  const addAction = mutations.projects.addActionToIssue.use({
    onSuccess: (newAction) => {
      updateIssue({
        ...issue,

        actions: [...(issue?.actions || []), newAction],
      } as FullProjectIssue);
    },
  });

  const addLabel = async (action: string, other?: string): Promise<void> => {
    if (typeof other === 'string' && !other.trim()) return;

    await addAction.execute({
      action,
      other,
      issue: issue!.id,
      project: issue!.project,
    });
  };

  const executeAddition = (action: string): void | Promise<void> => {
    if (action !== OTHER) return addLabel(action);

    openJustificationModal({
      title: t('complete-description') as string,
      text: t('provide-which-action-was-performed') as string,
      onConfirm: (other) => addLabel(action, other),
    });
  };

  const canAdd = [
    isIssueOpen(issue),
    (
      [
        issue?.owner,
        ...(issue?.salesInvolved.map((person) => person.user) || []),
        ...(issue?.executorsInvolved.map((person) => person.user) || []),
      ] as User[]
    ).some(({ id }) => id === user.id),
  ].every(Boolean);

  return (
    <Card flow="column">
      <Row gap="2">
        <ListToDoIcon />

        <span className="font-semibold">{t('actions-taken')}</span>

        {canAdd && (
          <DropdownMenu
            triggerIcon={<PlusIcon size={14} />}
            triggerClassName="ml-auto"
            align="end"
            className="max-h-[250px] overflow-y-auto"
          >
            {availableActions.map(({ getLabel, id }) => (
              <DropdownMenuItem key={id} onClick={() => executeAddition(id)}>
                {getLabel(t)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        )}
      </Row>

      {issue?.actions.map(({ id, action, other, addedAt, addedBy }, index) => (
        <div
          key={id}
          className="grid grid-cols-[30px_1fr_1fr] items-center gap-2 phone:grid-cols-[30px_1fr]"
        >
          <Box
            border="circle"
            size={22}
            className="bg-primary-light text-xs text-primary-foreground"
          >
            {index + 1}
          </Box>

          <Column gap="0">
            <span className="text-sm">{`${t(action)}${other ? ` (${other})` : ''}`}</span>

            <span className="text-xxs">{getTimestampDisplay(addedAt)}</span>
          </Column>

          <Row justifyContent="flex-end" className="phone:col-span-2">
            <UserAvatar user={addedBy} size={28} name nameSide="left" nameClassName="text-sm" />
          </Row>
        </div>
      ))}

      {addAction.loading && <Skeleton width="100%" height={32} />}
    </Card>
  );
}
